import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Understanding Environmental Well-Being',
    content: [
      '<strong>Learning Goal:</strong> Understand the meaning of environmental well-being, recognize how surroundings affect mental and physical health, and identify key environmental factors that shape daily quality of life.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Definition of environmental well-being',
      '• The 8 dimensions of environmental quality of life (WHOQOL-BREF)',
      '• How environment influences health and well-being',
      '• Environmental well-being as a human right',
      '<br/>',
      '<strong>Discussion:</strong> Environmental well-being refers to how your surroundings – physical, social, and economic – influence your quality of life. It\'s about living in safe, supportive, and sustainable spaces that help you thrive.'
    ]
  },
  {
    title: 'What is Environmental Well-Being?',
    content: [
      'Environmental well-being encompasses the quality of your physical and social surroundings and how they support your health and happiness. It\'s not just about nature – it includes your home, workplace, community, and access to essential services.',
      '<br/>',
      '<strong>Core Components:</strong>',
      '• <strong>Safety:</strong> Feeling secure in your environment',
      '• <strong>Physical Environment:</strong> Clean air, water, and manageable pollution/noise levels',
      '• <strong>Living Conditions:</strong> Adequate housing and comfortable spaces',
      '• <strong>Access to Services:</strong> Healthcare, transportation, and information',
      '• <strong>Financial Resources:</strong> Economic stability to maintain your environment',
      '• <strong>Leisure Opportunities:</strong> Access to recreation and relaxation spaces',
      '<br/>',
      '<strong>The Human Right Perspective:</strong> Access to a healthy environment is recognized as a fundamental human right by the United Nations. When your surroundings support your well-being, you can focus on growth rather than survival.'
    ]
  },
  {
    title: 'The 8 Dimensions of Environmental Quality of Life',
    content: [
      'The World Health Organization\'s Quality of Life assessment (WHOQOL-BREF) identifies 8 key environmental dimensions that contribute to overall well-being.',
      '<br/>',
          `<div class="module-table">
            <div class="table-row header-row">
              <div class="table-header pillar-cell">DIMENSION</div>
              <div class="table-header means-cell">WHAT IT MEASURES</div>
              <div class="table-header matters-cell">WHY IT MATTERS</div>
            </div>
            <div class="table-row">
              <div class="table-cell pillar-cell">Safety</div>
              <div class="table-cell means-cell">Personal security and protection from harm</div>
              <div class="table-cell matters-cell">Reduces stress and anxiety</div>
            </div>
            <div class="table-row">
              <div class="table-cell pillar-cell">Physical Environment</div>
              <div class="table-cell means-cell">Air quality, noise, climate, pollution</div>
              <div class="table-cell matters-cell">Affects respiratory and mental health</div>
            </div>
            <div class="table-row">
              <div class="table-cell pillar-cell">Financial Resources</div>
              <div class="table-cell means-cell">Economic means to maintain environment</div>
              <div class="table-cell matters-cell">Enables access to quality living</div>
            </div>
            <div class="table-row">
              <div class="table-cell pillar-cell">Health Services</div>
              <div class="table-cell means-cell">Access to medical care and facilities</div>
              <div class="table-cell matters-cell">Supports physical and mental health</div>
            </div>
            <div class="table-row">
              <div class="table-cell pillar-cell">Transportation</div>
              <div class="table-cell means-cell">Mobility and access to services</div>
              <div class="table-cell matters-cell">Enables independence and opportunity</div>
            </div>
            <div class="table-row">
              <div class="table-cell pillar-cell">Living Conditions</div>
              <div class="table-cell means-cell">Housing quality and comfort</div>
              <div class="table-cell matters-cell">Provides foundation for daily life</div>
            </div>
            <div class="table-row">
              <div class="table-cell pillar-cell">Information Access</div>
              <div class="table-cell means-cell">Access to knowledge and communication</div>
              <div class="table-cell matters-cell">Enables informed decision-making</div>
            </div>
            <div class="table-row">
              <div class="table-cell pillar-cell">Leisure Opportunities</div>
              <div class="table-cell means-cell">Access to recreation and relaxation</div>
              <div class="table-cell matters-cell">Supports mental health and balance</div>
            </div>
          </div>`,
      '<br/>',
      '<strong>Key Insight:</strong> These dimensions work together. Poor performance in one area (like transportation) can negatively impact others (like access to health services or leisure opportunities).'
    ]
  },
  {
    title: 'Environment and Health Connection',
    content: [
      'Your environment directly influences both your physical and mental health. Research shows that environmental factors can either support or undermine your well-being.',
      '<br/>',
      '<strong>Physical Health Impacts:</strong>',
      '• Clean air and water reduce respiratory and cardiovascular diseases',
      '• Safe neighborhoods encourage physical activity',
      '• Access to healthcare prevents minor issues from becoming major problems',
      '• Comfortable living conditions support immune function and recovery',
      '<br/>',
      '<strong>Mental Health Impacts:</strong>',
      '• Safe environments reduce anxiety and fear',
      '• Green spaces and nature exposure decrease stress and depression',
      '• Social environments affect feelings of belonging and support',
      '• Access to leisure activities provides stress relief and joy',
      '<br/>',
      '<strong>The Stress Factor:</strong> Environments that feel unsafe, polluted, or inadequate create chronic stress. This "environmental stress" can manifest as anxiety, poor sleep, weakened immunity, and reduced quality of life.'
    ]
  },
  {
    title: 'Environmental Awareness Map Activity',
    content: [
      'Take a moment to assess your current environments and their impact on your well-being.',
      '<br/>',
      '<strong>Activity Steps:</strong>',
      '1. <strong>Identify Your Daily Environments:</strong> List the main spaces you spend time in (home, work, commute, neighborhood, etc.)',
      '2. <strong>Rate Satisfaction:</strong> For each environment, rate your satisfaction on a scale of 1-5 (1 = very dissatisfied, 5 = very satisfied)',
      '3. <strong>Assess Impact:</strong> Note which spaces energize you and which drain you',
      '4. <strong>Identify Patterns:</strong> Look for common themes or environmental factors that affect multiple areas',
      '<br/>',
      '<strong>Reflection Questions:</strong>',
      '• Which environmental factors have the biggest impact on your daily mood?',
      '• Are there environments you can control that you\'ve been neglecting?',
      '• What small changes could improve your most draining environments?',
      '<br/>',
      '<strong>Take Action:</strong> Choose one environment to improve this week. Even small changes can have significant impacts on your well-being.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 1: Understanding Environmental Well-Being! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about environmental well-being and its impact on quality of life.',
        'The quiz will help you review key concepts about how your surroundings affect your health and happiness.'
      ]
    }
  }
];

const EnvironmentalModule1Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('environmentalM1');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/environmental-module-1/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/environmental-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left environmental-panel"
        >
          <div className="module-header">
            <h1>Module 1</h1>
            <p className="module-subtitle">Understanding Environmental Well-Being</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn about environmental well-being and how your surroundings impact your quality of life.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/environmental-module-1/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule1Page;

